import { Check, ListChecks, StickyNote } from "lucide-react";
import { PRIORITY_LABEL } from "@desk/contracts";
import type { TaskRowProps } from "../types";
import { PriorityIcon, StatusIcon } from "@/components/icons";
import { CarriedLabel, DeadlineLabel, RepeatBadge, RowHoverActions, SnoozeBadge, SyncBadge, TodayStar } from "@/components/task-bits";
import { cn } from "@/lib/utils";

/** Things 3-style rounded-square checkbox that fills charcoal. */
export function PaperCheckbox({
  checked,
  cancelled,
  onToggle,
  label,
  size = 18,
}: {
  checked: boolean;
  cancelled?: boolean;
  onToggle: () => void;
  label: string;
  size?: number;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      onKeyDown={(e) => e.stopPropagation()}
      style={{ width: size, height: size, borderRadius: Math.round(size * 0.26) }}
      className={cn(
        "group/cb relative inline-flex shrink-0 items-center justify-center border-[1.5px] transition-[background-color,border-color,transform] duration-150 active:scale-90",
        checked
          ? cancelled
            ? "border-subtle bg-subtle text-background"
            : "border-foreground bg-foreground text-background"
          : "border-foreground/30 bg-card/60 hover:border-foreground/70",
      )}
    >
      <Check
        strokeWidth={3}
        className={cn(
          "size-[11px] transition-opacity duration-150",
          checked ? "opacity-100" : "text-foreground/40 opacity-0 group-hover/cb:opacity-100",
        )}
      />
    </button>
  );
}

/** A row chip that opens its picker on click: a quiet hover tint, kept out of the tab order (S and P cover the keyboard). */
const chipButton =
  "-mx-1 inline-flex items-center rounded px-1 outline-none transition-colors hover:bg-foreground/[0.06] data-[state=open]:bg-foreground/[0.06]";

function Chip({ edit, label, children }: { edit?: (trigger: React.ReactElement) => React.ReactNode; label: string; children: React.ReactNode }) {
  if (!edit) return <>{children}</>;
  return edit(
    <button
      type="button"
      tabIndex={-1}
      aria-label={label}
      className={chipButton}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {children}
    </button>,
  );
}

export function PaperTaskRow(p: TaskRowProps) {
  const t = p.task;
  const closed = t.status === "done" || t.status === "cancelled";
  const doneItems = t.checklist.filter((c) => c.done).length;
  const indicators =
    !closed && (t.checklist.length > 0 || t.description) ? (
      <>
        {t.checklist.length > 0 && (
          <span className="inline-flex shrink-0 items-center gap-1 tabular" aria-label={`Checklist ${doneItems} of ${t.checklist.length}`}>
            <ListChecks className="size-3" aria-hidden />
            {doneItems}/{t.checklist.length}
          </span>
        )}
        {t.description && <StickyNote className="size-3 shrink-0" aria-label="Has notes" />}
      </>
    ) : null;
  return (
    <div
      {...p.rowProps}
      onClick={(e) => {
        p.rowProps.onClick?.(e);
        p.onSelect();
      }}
      onDoubleClick={p.onOpen}
      className={cn(
        "group/row relative flex min-h-10 cursor-default items-start gap-3 rounded-lg px-2.5 py-[8px] outline-none transition-colors duration-100",
        p.selected ? "bg-selected" : "hover:bg-hover",
        "focus-visible:ring-2 focus-visible:ring-ring/40",
      )}
    >
      <span className="flex h-[22px] items-center">
        <PaperCheckbox
          checked={closed}
          cancelled={t.status === "cancelled"}
          onToggle={p.onToggleComplete}
          label={closed ? `Reopen ${t.title}` : `Complete ${t.title}`}
        />
      </span>
      <button
        type="button"
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation();
          p.onSelect();
          p.onOpen();
        }}
        className="min-w-0 flex-1 cursor-default text-left"
      >
        <span className="flex items-start gap-1.5">
          {t.status === "in_progress" && !closed && <StatusIcon status="in_progress" className="mt-[3px] size-4 shrink-0" />}
          {p.plannedToday && !closed && <TodayStar className="mt-[4px] shrink-0" />}
          <span
            className={cn(
              "clamp-2 break-words text-[15px] leading-[22px]",
              closed ? "text-muted-foreground line-through decoration-foreground/25" : "text-foreground",
            )}
          >
            {t.title}
            {!p.location && indicators && <span className="ml-2 inline-flex translate-y-[-1px] items-center gap-2 align-middle text-[12px] text-subtle">{indicators}</span>}
          </span>
        </span>
        {p.location && (
          <span className="flex min-w-0 items-center gap-2 text-[12.5px] leading-[16px] text-subtle">
            <span className="flex min-w-0 items-center gap-1.5">
              {p.locationIcon}
              <span className="truncate">{p.location}</span>
            </span>
            {indicators}
          </span>
        )}
      </button>
      <div className="flex h-[22px] shrink-0 items-center gap-2.5 text-[12.5px] text-muted-foreground">
        <SyncBadge mark={p.sync} />
        <SnoozeBadge task={t} timezone={p.timezone} />
        <RepeatBadge task={t} />
        {p.carriedFrom && <CarriedLabel from={p.carriedFrom} today={p.today} />}
        {t.labels.length > 0 && !closed && (
          <span className="hidden lg:inline-flex">
            <Chip edit={p.chipEditors?.labels} label={`Labels: ${t.labels.join(", ")}`}>
              <span className="inline-flex items-center gap-1">
                {t.labels.slice(0, 2).map((l) => (
                  <span key={l} className="rounded-full bg-foreground/[0.06] px-2 text-[11.5px] leading-[18px] text-muted-foreground">
                    {l}
                  </span>
                ))}
              </span>
            </Chip>
          </span>
        )}
        {t.deadline && !closed && (
          <Chip edit={p.chipEditors?.deadline} label="Change deadline">
            <DeadlineLabel deadline={t.deadline} today={p.today} />
          </Chip>
        )}
        {t.status === "backlog" && (
          <Chip edit={p.chipEditors?.status} label="Status: Backlog">
            <StatusIcon status="backlog" className="size-3.5" />
          </Chip>
        )}
        {t.priority !== "none" && (
          <Chip edit={p.chipEditors?.priority} label={`Priority: ${PRIORITY_LABEL[t.priority]}`}>
            <PriorityIcon priority={t.priority} className="size-3.5" />
          </Chip>
        )}
        {t.readableId !== "DSK-0" && <span className="tabular hidden min-w-[52px] whitespace-nowrap text-right text-[12px] text-subtle md:inline">{t.readableId}</span>}
        {p.trailing}
        <span
          className={cn(
            "-mr-1 flex w-6 justify-center transition-opacity",
            p.selected ? "opacity-100" : "opacity-0 group-hover/row:opacity-100 focus-within:opacity-100",
          )}
        >
          {p.menu}
        </span>
      </div>
      <RowHoverActions selected={p.selected} className="right-[32px] top-[7px] h-6">
        {p.quickActions}
      </RowHoverActions>
    </div>
  );
}
